import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardGrid from './DashboardGrid';
import WidgetSelector from './WidgetSelector';
import WidgetConfigModal from './WidgetConfigModal';
import ProDashboardPromo from './ProDashboardPromo';
import '../../styles/ProDashboard.css';

const API_URL = 'https://iracing6-backend.herokuapp.com';

function ProDashboard() {
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [pendingWidget, setPendingWidget] = useState(null);
  const [showPromo, setShowPromo] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestEmail, setDigestEmail] = useState('');
  const [digestTimeLocal, setDigestTimeLocal] = useState(7);
  const [accountEmail, setAccountEmail] = useState('');
  const [digestSaveStatus, setDigestSaveStatus] = useState(null);
  const [digestCollapsed, setDigestCollapsed] = useState(true);

  const tzOffsetHours = new Date().getTimezoneOffset() / 60;
  const utcToLocal = (utcH) => ((utcH - tzOffsetHours) % 24 + 24) % 24;
  const localToUTC = (localH) => ((localH + tzOffsetHours) % 24 + 24) % 24;
  const formatLocalHour = (h) => {
    const period = h >= 12 ? 'PM' : 'AM';
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${display}:00 ${period}`;
  };

  // Auth check and fetch dashboard
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isPaidUser = localStorage.getItem('isPaidUser') === 'true';

    if (!token || !isPaidUser) {
      setShowPromo(true);
      setLoading(false);
      return;
    }

    fetchDashboard(token);
    fetchDigestStatus(token);
  }, [navigate]);

  const fetchDashboard = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/user-dashboard/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 403) {
        // User is no longer a Pro user - update localStorage
        localStorage.setItem('isPaidUser', 'false');
        setShowPromo(true);
        setLoading(false);
        return;
      }

      if (response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('isPaidUser');
        setShowPromo(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard');
      }

      const data = await response.json();
      setWidgets(data.widgets || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const saveDashboard = async (newWidgets, skipStateUpdate = false) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/user-dashboard/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ widgets: newWidgets }),
      });

      if (!response.ok) {
        throw new Error('Failed to save dashboard');
      }

      const data = await response.json();
      // Only update state if not skipping (for optimistic updates like expand)
      if (!skipStateUpdate) {
        setWidgets(data.widgets || []);
      }
    } catch (err) {
      console.error('Error saving dashboard:', err);
      setError(err.message);
      // On error, refetch to get correct state
      fetchDashboard(token);
    }
  };

  const fetchDigestStatus = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/user-dashboard/email-digest`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDigestEnabled(data.emailDigestEnabled || false);
        setDigestEmail(data.emailDigestAddress || '');
        const serverUTC = data.emailDigestTimeUTC != null ? data.emailDigestTimeUTC : 12;
        setDigestTimeLocal(utcToLocal(serverUTC));
        setAccountEmail(data.username || '');
      }
    } catch (err) {
      console.error('Error fetching digest status:', err);
    }
  };

  const saveDigestSettings = async ({ enabled, email, timeLocal }) => {
    const token = localStorage.getItem('token');
    setDigestLoading(true);
    setDigestSaveStatus(null);
    try {
      const response = await fetch(`${API_URL}/api/user-dashboard/email-digest`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled, email, timeUTC: localToUTC(timeLocal) }),
      });
      if (response.ok) {
        const data = await response.json();
        setDigestEnabled(data.emailDigestEnabled);
        setDigestEmail(data.emailDigestAddress || '');
        const returnedUTC = data.emailDigestTimeUTC != null ? data.emailDigestTimeUTC : 12;
        setDigestTimeLocal(utcToLocal(returnedUTC));
        setDigestSaveStatus('saved');
        setDigestCollapsed(true);
        setTimeout(() => setDigestSaveStatus(null), 2000);
      } else {
        setDigestSaveStatus('error');
      }
    } catch (err) {
      console.error('Error saving digest settings:', err);
      setDigestSaveStatus('error');
    }
    setDigestLoading(false);
  };

  const handleDigestToggle = () => {
    const newValue = !digestEnabled;
    setDigestEnabled(newValue);
    saveDigestSettings({
      enabled: newValue,
      email: digestEmail || null,
      timeLocal: digestTimeLocal,
    });
  };

  const handleDigestSave = () => {
    saveDigestSettings({
      enabled: digestEnabled,
      email: digestEmail || null,
      timeLocal: digestTimeLocal,
    });
  };

  const handleSlotClick = (position) => {
    const existingWidget = widgets.find(w => w.position === position);
    if (!existingWidget) {
      setSelectedPosition(position);
      setSelectorOpen(true);
    }
  };

  const handleWidgetSelect = (widgetType) => {
    setSelectorOpen(false);

    // Check if this widget type needs configuration
    const needsConfig = ['irating_distribution', 'lap_times_boxplot', 'career_stats'].includes(widgetType);

    if (needsConfig) {
      setPendingWidget({ widgetType, position: selectedPosition });
      setConfigModalOpen(true);
    } else {
      // Add widget directly without config
      const newWidget = {
        widgetType,
        position: selectedPosition,
        config: {},
      };
      const newWidgets = [...widgets.filter(w => w.position !== selectedPosition), newWidget];
      saveDashboard(newWidgets);
    }
  };

  const handleConfigSave = (config) => {
    if (pendingWidget) {
      const newWidget = {
        ...pendingWidget,
        config,
      };
      const newWidgets = [...widgets.filter(w => w.position !== pendingWidget.position), newWidget];
      saveDashboard(newWidgets);
    }
    setConfigModalOpen(false);
    setPendingWidget(null);
  };

  const handleConfigCancel = () => {
    setConfigModalOpen(false);
    setPendingWidget(null);
  };

  const handleRemoveWidget = (position) => {
    const newWidgets = widgets.filter(w => w.position !== position);
    saveDashboard(newWidgets);
  };

  const handleMoveWidget = (fromPosition, toPosition) => {
    if (fromPosition === toPosition) return;

    const fromWidget = widgets.find(w => w.position === fromPosition);
    const toWidget = widgets.find(w => w.position === toPosition);

    if (!fromWidget) return;

    let newWidgets;
    if (toWidget) {
      // Swap positions
      newWidgets = widgets.map(w => {
        if (w.position === fromPosition) return { ...w, position: toPosition };
        if (w.position === toPosition) return { ...w, position: fromPosition };
        return w;
      });
    } else {
      // Move to empty slot
      newWidgets = widgets.map(w => {
        if (w.position === fromPosition) return { ...w, position: toPosition };
        return w;
      });
    }

    setWidgets(newWidgets);
    saveDashboard(newWidgets, true);
  };

  const handleExpandWidget = (position) => {
    const widget = widgets.find(w => w.position === position);
    if (!widget) return;

    const currentExpandState = widget.expandState || 0;
    const newExpandState = (currentExpandState + 1) % 3; // Cycle: 0 -> 1 -> 2 -> 0

    const newWidgets = widgets.map(w => {
      if (w.position === position) {
        return { ...w, expandState: newExpandState };
      }
      return w;
    });

    setWidgets(newWidgets);
    saveDashboard(newWidgets, true); // Skip state update to keep optimistic update
  };

  // Debounced config change handler for widget filter persistence
  const saveTimerRef = useRef(null);
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;

  const handleConfigChange = useCallback((position, newConfig) => {
    const updatedWidgets = widgetsRef.current.map(w => {
      if (w.position === position) {
        return { ...w, config: newConfig };
      }
      return w;
    });
    setWidgets(updatedWidgets);
    widgetsRef.current = updatedWidgets;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveDashboard(widgetsRef.current, true);
    }, 500);
  }, []);

  if (showPromo) {
    return <ProDashboardPromo />;
  }

  if (loading) {
    return (
      <div className="pro-dashboard-loading">
        <div className="spinner"></div>
        <div className="loading-text">Loading your dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pro-dashboard-error">
        <h2>Error loading dashboard</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="pro-dashboard">
      <div className="pro-dashboard-header">
        <h1><img src={`${process.env.PUBLIC_URL}/linechart_color.ico`} alt="" className="pro-header-icon" />Pro Dashboard</h1>
        <p>Customize your dashboard with your favorite charts and widgets</p>
      </div>

      <div className="digest-section">
        <div className="digest-section-row digest-section-top">
          <label className="digest-toggle">
            <input
              type="checkbox"
              checked={digestEnabled}
              onChange={handleDigestToggle}
              disabled={digestLoading}
            />
            <span className="digest-toggle-label">Receive daily email of dashboard</span>
          </label>
          {digestSaveStatus === 'saved' && <span className="digest-saved">Saved</span>}
          {digestSaveStatus === 'error' && <span className="digest-error">Error saving</span>}
          {digestEnabled && (
            <button
              className="digest-caret"
              onClick={() => setDigestCollapsed(!digestCollapsed)}
              aria-label={digestCollapsed ? 'Expand settings' : 'Collapse settings'}
            >
              <span className={`digest-caret-icon ${digestCollapsed ? '' : 'digest-caret-open'}`}>&#9662;</span>
            </button>
          )}
        </div>

        {digestEnabled && !digestCollapsed && (
          <div className="digest-settings">
            <div className="digest-field">
              <label className="digest-field-label">Send to</label>
              <input
                type="email"
                className="digest-email-input"
                value={digestEmail}
                onChange={(e) => setDigestEmail(e.target.value)}
                placeholder={accountEmail || 'your@email.com'}
              />
              <span className="digest-field-hint">
                {digestEmail ? '' : `Defaults to your account email (${accountEmail})`}
              </span>
            </div>

            <div className="digest-field">
              <label className="digest-field-label">Send time</label>
              <select
                className="digest-time-select"
                value={digestTimeLocal}
                onChange={(e) => setDigestTimeLocal(parseInt(e.target.value, 10))}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {formatLocalHour(h)}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="digest-save-btn"
              onClick={handleDigestSave}
              disabled={digestLoading}
            >
              {digestLoading ? 'Saving...' : 'Save preferences'}
            </button>
          </div>
        )}
      </div>

      <DashboardGrid
        widgets={widgets}
        onSlotClick={handleSlotClick}
        onRemoveWidget={handleRemoveWidget}
        onExpandWidget={handleExpandWidget}
        onMoveWidget={handleMoveWidget}
        onConfigChange={handleConfigChange}
      />

      {selectorOpen && (
        <WidgetSelector
          onSelect={handleWidgetSelect}
          onClose={() => setSelectorOpen(false)}
        />
      )}

      {configModalOpen && pendingWidget && (
        <WidgetConfigModal
          widgetType={pendingWidget.widgetType}
          onSave={handleConfigSave}
          onCancel={handleConfigCancel}
        />
      )}
    </div>
  );
}

export default ProDashboard;
